import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    User, ArrowLeft, MapPin, TrendingUp, TrendingDown, Activity, Award,
    FileText, Building2, Wallet, AlertCircle, CheckCircle, Layers,
} from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
        return v.toLocaleString("en-IN");
    }
    return String(v);
};

const KpiTile = ({ label, value, sub, tone = "white", testId }) => (
    <Panel testId={testId}>
        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">{label}</div>
        <div className={`mt-1 text-2xl font-display font-black text-${tone === "white" ? "white" : `[var(--${tone})]`}`}>{value}</div>
        {sub && <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">{sub}</div>}
    </Panel>
);

export default function Client360() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [bills, setBills] = useState(null);
    const [ageing, setAgeing] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [tab, setTab] = useState("overview");
    const nav = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get(`/clients/${id}/360`),
            api.get(`/clients/${id}/ledger`),
            api.get(`/billing/register?client_id=${id}`),
            api.get(`/ageing?client_id=${id}`),
            api.get(`/clients/cashflow-forecast?client_id=${id}&horizon_days=90`),
        ]).then(([a, b, c, d, e]) => {
            setData(a.data);
            setLedger(b.data);
            setBills(c.data);
            setAgeing(d.data);
            setForecast(e.data);
        }).catch(err => toast.error("Load failed: " + (err.response?.data?.detail || err.message)));
    }, [id]);

    if (!data) return <div className="p-4 text-[var(--text-secondary)] font-mono-fin uppercase text-xs">Loading Client 360°…</div>;

    const c = data.client;
    const s = data.summary;
    const projects = data.projects || [];
    const contracts = data.contracts || [];

    const toneRisk = s.risk_score >= 80 ? "pos" : (s.risk_score >= 60 ? "brand-amber" : "neg");

    return (
        <div className="space-y-4">
            <button
                data-testid="back-btn"
                onClick={() => nav("/clients")}
                className="text-[var(--text-secondary)] hover:text-[var(--brand-amber)] flex items-center gap-1 text-xs font-mono-fin uppercase"
            >
                <ArrowLeft size={12} /> Portfolio
            </button>

            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// CLIENT 360°</div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h1 className="font-display font-black text-3xl flex items-center gap-3">
                            <User className="text-[var(--brand-amber)]" size={26} />
                            {c.name}
                        </h1>
                        <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider flex items-center gap-3 flex-wrap">
                            <span className="text-[var(--brand-amber)]">{c.id}</span>
                            <span>· {c.type}</span>
                            <span>· {c.sector}</span>
                            {c.pan && <span>· PAN {c.pan}</span>}
                            {c.gstin && <span>· GSTIN {c.gstin}</span>}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Risk Band</div>
                        <div className={`text-2xl font-display font-black text-[var(--${toneRisk})]`}>{s.risk_band}</div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">Score {s.risk_score}/100</div>
                    </div>
                </div>
            </div>

            {/* Section 24 — One-screen KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="client-360-kpis">
                <KpiTile testId="kpi-contract" label="Contract Value" value={fmt(s.contract_value)} sub={`${projects.length} project(s)`} />
                <KpiTile testId="kpi-executed" label="Work Executed" value={fmt(s.work_executed)} sub={`${s.completion_pct}% complete`} />
                <KpiTile testId="kpi-remaining" label="Work Remaining" value={fmt(s.work_remaining)} sub={s.work_wip > 0 ? `+ ${fmt(s.work_wip)} WIP` : "no WIP"} />
                <KpiTile testId="kpi-billed" label="Billed" value={fmt(s.billed)} />
                <KpiTile testId="kpi-collected" label="Collected" value={fmt(s.collected)} tone="pos" />
                <KpiTile testId="kpi-receivable" label="Receivable" value={fmt(s.receivable)} tone="brand-amber" sub={`Coll Eff ${s.collection_eff}%`} />
                <KpiTile testId="kpi-actual-cost" label="Actual Cost" value={fmt(s.actual_cost)} />
                <KpiTile testId="kpi-profit" label="Expected Profit" value={fmt(s.expected_profit)} tone={s.expected_profit > 0 ? "pos" : "neg"} sub={`Margin ${s.margin_pct}%`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Client details */}
                <Panel testId="client-details" title="Client Details">
                    <table className="w-full text-xs font-body">
                        <tbody>
                            {[
                                ["Client Type", c.type],
                                ["Sector", c.sector],
                                ["Contact", c.contact_person || "—"],
                                ["Designation", c.designation || "—"],
                                ["Phone", c.phone || "—"],
                                ["Email", c.email || "—"],
                                ["Billing Address", c.billing_address || "—"],
                                ["Site Address", c.shipping_address || "—"],
                                ["PAN", c.pan || "—"],
                                ["GSTIN", c.gstin || "—"],
                                ["Credit Days", c.credit_days ? `${c.credit_days} days` : "—"],
                                ["Credit Limit", fmt(c.credit_limit)],
                                ["Retention %", `${c.retention_pct || 0}%`],
                                ["TDS Receivable %", `${c.tds_receivable_rate || 0}%`],
                                ["Rating", c.rating || "—"],
                                ["Onboarded", c.onboarded || "—"],
                            ].map(([k, v]) => (
                                <tr key={k} className="border-b border-panel/60">
                                    <td className="py-1.5 pr-2 text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">{k}</td>
                                    <td className="py-1.5 text-[var(--text-primary)]/90 text-[11px] text-right">{v}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {c.notes && (
                        <div className="mt-2 text-[10px] font-body text-[var(--text-primary)]/60 italic border-t border-panel/60 pt-2">
                            📝 {c.notes}
                        </div>
                    )}
                </Panel>

                {/* Projects */}
                <div className="lg:col-span-2 space-y-4">
                    <Panel testId="projects-list" title={`Projects · ${projects.length}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Project ID</th>
                                        <th className="text-left py-2 px-2">Name</th>
                                        <th className="text-right py-2 px-2">Contract</th>
                                        <th className="text-right py-2 px-2">Billed</th>
                                        <th className="text-right py-2 px-2">Collected</th>
                                        <th className="text-left py-2 px-2">Stage</th>
                                        <th className="text-right py-2 px-2">%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-[var(--text-muted)]">No projects.</td></tr>}
                                    {projects.map((p) => (
                                        <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b border-panel/60">
                                            <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{p.id}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/90">{p.name}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(p.contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(p.billed)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--pos)]">{fmt(p.collected)}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/70 text-[10px]">{p.current_stage || "—"}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{p.completion_pct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel testId="contracts-list" title={`Contracts · ${contracts.length}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Contract ID</th>
                                        <th className="text-left py-2 px-2">Agreement No</th>
                                        <th className="text-right py-2 px-2">Original</th>
                                        <th className="text-right py-2 px-2">Gross (+GST)</th>
                                        <th className="text-right py-2 px-2">Revised</th>
                                        <th className="text-right py-2 px-2">Final</th>
                                        <th className="text-left py-2 px-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contracts.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-[var(--text-muted)]">No contracts.</td></tr>}
                                    {contracts.map((ct) => (
                                        <tr key={ct.id} data-testid={`contract-row-${ct.id}`} className="border-b border-panel/60">
                                            <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{ct.id}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/90">{ct.agreement_no}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(ct.original_contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(ct.gross_contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(ct.revised_contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(ct.final_contract_value)}</td>
                                            <td className="py-1.5 px-2 text-[11px]">{ct.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
            </div>

            {/* Section 26 · Reconciliation formula */}
            <Panel testId="reconciliation-formula" title="Client Accounting Reconciliation (Section 26)">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs font-mono-fin">
                    <div className="space-y-1">
                        <div className="text-[var(--text-secondary)] uppercase tracking-wider text-[10px] mb-2">Formula</div>
                        {[
                            ["Contract Value", s.contract_value],
                            ["+ Extra Work", s.extra_work],
                            ["+ Variation Orders", 0],
                            ["- Deductions", 0],
                            ["Revised Contract", s.contract_value + (s.extra_work || 0)],
                            ["- Executed", s.work_executed],
                            ["- Received", s.collected],
                            ["- TDS", s.tds_deducted],
                            ["- Retention", s.retention],
                            ["Closing Receivable", s.receivable],
                        ].map(([k, v], i) => (
                            <div key={k} className={`flex justify-between border-b border-panel/40 py-1 ${k.startsWith("Revised") || k.startsWith("Closing") ? "font-bold text-[var(--text-primary)] border-panel" : "text-[var(--text-primary)]/80"}`}>
                                <span>{k}</span>
                                <span className={i === 9 ? "text-[var(--brand-amber)]" : ""}>{fmt(v)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-1">
                        <div className="text-[var(--text-secondary)] uppercase tracking-wider text-[10px] mb-2">4-Way Reconciliation (Section 7)</div>
                        <div className="border border-panel p-3 space-y-2">
                            <div className="flex justify-between">
                                <span className="text-[var(--text-primary)]/80">A. Work Done</span>
                                <span className="text-[var(--text-primary)] font-bold">{fmt(s.work_executed)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[var(--text-primary)]/80">B. Work Billed</span>
                                <span className="text-[var(--text-primary)] font-bold">{fmt(s.billed)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[var(--text-primary)]/80">C. Amount Received</span>
                                <span className="text-[var(--pos)] font-bold">{fmt(s.collected)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[var(--text-primary)]/80">D. Balance Receivable</span>
                                <span className="text-[var(--brand-amber)] font-bold">{fmt(s.receivable)}</span>
                            </div>
                            <div className="border-t border-panel/60 pt-2 mt-2 space-y-1 text-[11px]">
                                <div className="flex justify-between text-[var(--text-primary)]/60">
                                    <span>Unbilled Revenue (A - B)</span>
                                    <span>{fmt(s.work_executed - s.billed)}</span>
                                </div>
                                <div className="flex justify-between text-[var(--text-primary)]/60">
                                    <span>Trade Receivable (B - C)</span>
                                    <span>{fmt(s.billed - s.collected)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Panel>

            {/* ==================== PHASE B · LEDGER / BILLS / AGEING / FORECAST ==================== */}
            <div className="flex border-b border-panel">
                {[
                    ["overview", "Overview"],
                    ["ledger", `Ledger${ledger ? ` · ${ledger.summary.line_count}` : ""}`],
                    ["bills", `Bills${bills ? ` · ${bills.summary.count}` : ""}`],
                    ["ageing", "Ageing"],
                    ["forecast", "Cashflow"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        data-testid={`c360-tab-${key}`}
                        onClick={() => setTab(key)}
                        className={`px-4 py-2 text-xs font-mono-fin uppercase tracking-wider border-b-2 ${tab === key ? "border-[var(--brand-amber)] text-[var(--brand-amber)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === "ledger" && ledger && (
                <Panel testId="c360-ledger" title="Client Ledger (Section 4)" subtitle={`Closing balance: ${fmt(ledger.summary.closing_balance)}`}>
                    <div className="overflow-x-auto max-h-[500px]">
                        <table className="w-full text-xs font-body">
                            <thead className="sticky top-0 bg-panel">
                                <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 px-2">Date</th>
                                    <th className="text-left py-2 px-2">Type</th>
                                    <th className="text-left py-2 px-2">Ref</th>
                                    <th className="text-left py-2 px-2">Description</th>
                                    <th className="text-right py-2 px-2">Debit</th>
                                    <th className="text-right py-2 px-2">Credit</th>
                                    <th className="text-right py-2 px-2">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ledger.lines.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--text-muted)]">No transactions in ledger.</td></tr>}
                                {ledger.lines.map((l, i) => (
                                    <tr key={i} data-testid={`ledger-row-${i}`} className="border-b border-panel/60">
                                        <td className="py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/80">{l.date}</td>
                                        <td className="py-1.5 px-2 text-[10px]">
                                            <span className={`font-mono-fin uppercase ${
                                                l.kind === "bill" ? "text-[var(--brand-amber)]" :
                                                l.kind === "receipt" ? "text-[var(--pos)]" :
                                                l.kind === "tds" ? "text-blue-400" : "text-purple-400"
                                            }`}>{l.kind}</span>
                                        </td>
                                        <td className="py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/70 text-[10px]">{l.ref}</td>
                                        <td className="py-1.5 px-2 text-[var(--text-primary)]/90 text-[11px]">{l.description}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin">{l.debit ? fmt(l.debit) : "—"}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--pos)]">{l.credit ? fmt(l.credit) : "—"}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)] font-bold">{fmt(l.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            )}

            {tab === "bills" && bills && (
                <Panel testId="c360-bills" title={`Bills · ${bills.summary.count}`} subtitle={`Outstanding: ${fmt(bills.summary.total_outstanding)} · Overdue: ${bills.summary.overdue_count}`}>
                    <div className="overflow-x-auto max-h-[500px]">
                        <table className="w-full text-xs font-body">
                            <thead className="sticky top-0 bg-panel">
                                <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 px-2">Invoice</th>
                                    <th className="text-left py-2 px-2">Date</th>
                                    <th className="text-left py-2 px-2">Due</th>
                                    <th className="text-right py-2 px-2">Gross</th>
                                    <th className="text-right py-2 px-2">Collected</th>
                                    <th className="text-right py-2 px-2">Outstanding</th>
                                    <th className="text-right py-2 px-2">Days Late</th>
                                    <th className="text-left py-2 px-2">Bucket</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bills.items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[var(--text-muted)]">No bills.</td></tr>}
                                {bills.items.map((b, i) => (
                                    <tr key={i} data-testid={`bill-row-${b.invoice_no}`} className="border-b border-panel/60">
                                        <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{b.invoice_no}</td>
                                        <td className="py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/80">{b.bill_date}</td>
                                        <td className="py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/70">{b.due_date}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(b.gross)}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--pos)]">{fmt(b.collected)}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(b.outstanding)}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin">
                                            <span className={b.days_overdue > 60 ? "text-[var(--neg)]" : b.days_overdue > 0 ? "text-[var(--brand-amber)]" : "text-[var(--text-primary)]/60"}>
                                                {b.days_overdue > 0 ? `${b.days_overdue}d` : "—"}
                                            </span>
                                        </td>
                                        <td className="py-1.5 px-2 text-[10px] font-mono-fin uppercase">
                                            <span className={b.bucket === "paid" ? "text-[var(--pos)]" : b.bucket === "180+" ? "text-[var(--neg)]" : "text-[var(--brand-amber)]"}>{b.bucket}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            )}

            {tab === "ageing" && ageing && (
                <Panel testId="c360-ageing" title="Ageing (Section 17)" subtitle={`Total outstanding: ${fmt(ageing.summary.total_outstanding)}`}>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                        {Object.entries(ageing.buckets).map(([k, v]) => k !== "paid" && (
                            <Panel key={k} testId={`ageing-bucket-${k}`}>
                                <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">{k}</div>
                                <div className={`text-lg font-display font-black mt-1 ${
                                    k === "current" ? "text-[var(--text-primary)]/60" :
                                    k === "0-30" ? "text-[var(--text-primary)]" :
                                    k === "31-60" ? "text-[var(--brand-amber)]" :
                                    k === "61-90" ? "text-[var(--brand-amber)]" :
                                    k === "91-180" ? "text-[var(--neg)]" : "text-[var(--neg)]"
                                }`}>{fmt(v)}</div>
                            </Panel>
                        ))}
                    </div>
                </Panel>
            )}

            {tab === "forecast" && forecast && (
                <Panel testId="c360-forecast" title="Cash-flow Forecast (Section 18)" subtitle={`Expected in next ${forecast.summary.horizon_days} days: ${fmt(forecast.summary.total_forecasted)}`}>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                        {Object.entries(forecast.buckets_cumulative).map(([k, v]) => (
                            <Panel key={k} testId={`forecast-${k}`}>
                                <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">{k.replace("next_", "Next ").replace("d", " days")}</div>
                                <div className="text-lg font-display font-black text-[var(--brand-amber)] mt-1">{fmt(v)}</div>
                                <div className="text-[9px] text-[var(--text-muted)] font-mono-fin">cumulative</div>
                            </Panel>
                        ))}
                    </div>
                    {forecast.beyond_horizon > 0 && (
                        <div className="text-xs font-mono-fin text-[var(--text-secondary)] border-t border-panel pt-2">
                            Beyond {forecast.summary.horizon_days}d horizon: <span className="text-[var(--neg)]">{fmt(forecast.beyond_horizon)}</span>
                        </div>
                    )}
                </Panel>
            )}
        </div>
    );
}
